import { useCallback, useEffect, useRef, useState } from "react";
import { signMessage } from "@hoa/crypto";
import type { Experience, Fix, ServerMessage, SessionSummary, Stage, TribePrivate } from "@hoa/protocol";
import { annonceDuServeur, fermerAnnonce } from "../annonce.js";
import { SessionSocket, ecrireTribuEnCours, lireTribuEnCours, oublierTribuEnCours } from "../net.js";
import { currentFix, keepAwake, watchFix } from "../adapters/geo.js";
import { serverNow } from "../adapters/audio.js";
import {
  addReceipt,
  enregistrerEmail,
  loadAccount,
  setPseudo as enregistrerPseudoCompte,
  type LocalAccount,
} from "../adapters/account.js";
import {
  connecterCompte,
  creerCompte,
  deconnecterCompte,
  pseudoDuCompte,
  secoursCompte,
  supprimerCompte,
  verifierCompte,
  boutique as boutiqueServeur,
  contester as contesterServeur,
  exporterCompte,
  signalerSalle,
  photoDuCompte,
} from "../adapters/compte-serveur.js";
import { CleSecours, Connexion, CreerCompte, FeuilleEffacerAppareil, FeuilleSupprimer, Secours, type Envoi } from "./compte.js";
import { effacerAppareil } from "./appareil.js";
import { Creer } from "./creer.js";
import {
  Autres,
  BarreSoiree,
  CompteRequis,
  FeuilleArrondissements,
  FondGrise,
  HorsSession,
  Intro,
  Moi,
  Parametres,
  Proposition,
  Quitte,
  Recap,
  duSecteur,
  meilleureTribu,
  type SecteurCompte,
} from "./ecrans.js";
import { Annulee, Tribu, type ChoixTribu, type LigneChat, type PointsCommuns } from "./Tribu.js";
import { EcranEvenement, EcranSansEvenement, type EvenementPublic } from "./evenement.js";
import { evenementCourant, poserEvenement } from "./voix.js";
import { poserProfil, profilCourant, sanctionCourante, useProfil, type Badge } from "./profil.js";
import { direHoa, taireHoa } from "./parole.js";
import { poserPhoto, usePhoto } from "./photo.js";
import { FeuilleSignalerSalle, FeuilleSoirees, Personnaliser } from "./ecrans3.js";
import { bip, demanderNotifications, ecrireSessionPrevenue, lireSessionPrevenue, notifier, prevenirArrivee } from "./prevenir.js";
import {
  Badges,
  Boutique,
  EtapesAVenir,
  Garder,
  Prevenu,
  Regles as ReglesHoa,
  RelireSoiree,
  Suspendu,
  type Planning,
} from "./ecrans2.js";
import { gainPour, garderSoiree, lireSoirees, totalPoints, type SoireePassee } from "./historique.js";
import { SKINS, appliquerBarre, ecrireSkin, lireSkin, type SkinId } from "./skins.js";
import { Barre, Bulle, Ecran, Etape, MenuMoi, Retour, aHeure, ecouterTripleHoa, heure, type EntreeMenu } from "./ui.js";
import { BoutonDemo, useModeDemo } from "./demo.js";
import { ESPACES_HUMAINS } from "../espaces.js";
import "./hoa.css";

type Vue =
  | "chargement"
  | "intro"
  | "proposition"
  | "autres"
  | "creer"
  | "moi"
  | "parametres"
  | "recap"
  | "tribu"
  | "quitte"
  | "compte"
  | "connexion"
  | "secours"
  | "cle"
  | "evenement"
  | "boutique"
  | "badges"
  | "suspendu"
  | "prevenu"
  | "relire"
  | "regles"
  | "personnaliser"
  /** Dans une tribu : sa frise, rouverte sur la liste des tribus (changer de tribu). */
  | "tribus";

interface Secteur {
  id: string;
  name: string;
}

const CLE_INTRO = "hoa.intro";
const CLE_PSEUDO = "hoa.pseudo";
const CLE_SECTEUR = "hoa.secteur";
/** Les salles ouvertes par la régie : on y arrive par trois appuis sur Hoa. */
const REGIE: Secteur = { id: "regie", name: "la régie" };

function lire(cle: string): string | null {
  try {
    return localStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string | null): void {
  try {
    if (valeur === null) localStorage.removeItem(cle);
    else localStorage.setItem(cle, valeur);
  } catch {
    /* navigation privée : la valeur vaut pour la visite */
  }
}

async function json<T>(chemin: string, init?: RequestInit): Promise<T> {
  const r = await fetch(chemin, { ...init, headers: { "content-type": "application/json", ...(init?.headers ?? {}) } });
  const corps = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(corps.error ?? `Erreur ${r.status}`);
  return corps;
}

const ERREURS_FATALES = ["complet", "annulee", "session", "protocole", "drainage", "ordre", "suspendu", "banni"];

/**
 * Un identifiant tiré au hasard, gardé sur le téléphone : il ne sert qu'à
 * compter les présents dans la zone d'un événement. Rien ne le relie à une
 * personne, un compte ou une position gardée.
 */
function visiteur(): string {
  let v = lire("hoa.visiteur");
  if (!v) {
    const b = new Uint8Array(8);
    crypto.getRandomValues(b);
    v = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
    ecrire("hoa.visiteur", v);
  }
  return v;
}

async function evenementsProches(f: Fix): Promise<EvenementPublic[]> {
  try {
    const r = await json<{ evenements: EvenementPublic[] }>("/api/evenements/proche", {
      method: "POST",
      body: JSON.stringify({ fix: { lat: f.lat, lon: f.lon }, visiteur: visiteur() }),
    });
    return r.evenements;
  } catch {
    return [];
  }
}

/**
 * Le parcours participant de Hoa : première ouverture, proposition, tribu.
 *
 * Tout ce qui décide du moment de la soirée vient du serveur (la phase de la
 * tribu, l'étape du participant) ; cet écran ne fait que la raconter. Il tient
 * aussi ce qui se pose par-dessus n'importe quel écran : le menu « Moi » et la
 * feuille des arrondissements.
 */
export function Soiree(props: {
  account: LocalAccount | null;
  sessionId?: string;
  onAccount: (a: LocalAccount) => void;
  onReceipt: () => void;
  /** Ouvrir directement un écran (l'ancienne adresse « #/compte » mène à Paramètres). */
  ouvrir?: "parametres";
}): JSX.Element {
  const [skin, setSkinEtat] = useState<SkinId>(lireSkin);
  useEffect(() => {
    appliquerBarre(skin);
    // Le fond de la page elle-même prend la couleur du thème : rien d'autre
    // n'apparaît quand le navigateur redimensionne pour le clavier.
    const fond = SKINS.find((x) => x.id === skin)?.apercu[0] ?? "#161616";
    document.documentElement.style.background = fond;
    document.body.style.background = fond;
    // #root aussi : c'est lui qu'on voit sous l'application quand le clavier
    // s'ouvre (la bande bleue sur iPhone venait de son fond par défaut).
    const racineReact = document.getElementById("root");
    if (racineReact) racineReact.style.background = fond;
  }, [skin]);

  // Le clavier : l'application se cale sur la partie visible de l'écran.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const racine = document.documentElement;
    const maj = (): void => {
      racine.style.setProperty("--hoa-hauteur", `${Math.round(vv.height)}px`);
      racine.style.setProperty("--hoa-haut", `${Math.round(vv.offsetTop)}px`);
    };
    maj();
    vv.addEventListener("resize", maj);
    vv.addEventListener("scroll", maj);
    return () => {
      vv.removeEventListener("resize", maj);
      vv.removeEventListener("scroll", maj);
    };
  }, []);
  const changerSkin = (s: SkinId): void => {
    ecrireSkin(s);
    setSkinEtat(s);
  };

  const [maintenant, setMaintenant] = useState(() => serverNow());
  useEffect(() => {
    const id = window.setInterval(() => setMaintenant(serverNow()), 1000);
    return () => window.clearInterval(id);
  }, []);

  /*
   * La navigation : « retour » ramène toujours à l'écran d'avant, pas à
   * l'accueil. Chaque changement d'écran empile celui qu'on quitte, et pose
   * une entrée dans l'historique du navigateur : le geste « retour » du
   * téléphone fait la même chose que le bouton de l'écran. Un écran qui n'a
   * plus de sens (une tribu terminée, un formulaire de compte déjà rempli)
   * est sauté en revenant.
   */
  const [vue, setVueEtat] = useState<Vue>("chargement");
  const vueRef = useRef<Vue>(vue);
  const pile = useRef<Vue[]>([]);
  const entreesHistorique = useRef(0);
  const retournable = useRef<(v: Vue) => boolean>(() => true);
  const setVue = useCallback((v: Vue): void => {
    const avant = vueRef.current;
    if (v === avant) return;
    // Pas deux fois le même écran de suite dans la pile : le retour ferait du sur-place.
    if (avant !== "chargement" && avant !== "intro" && pile.current[pile.current.length - 1] !== avant) {
      pile.current.push(avant);
      if (pile.current.length > 40) pile.current.shift();
      try {
        history.pushState({ hoa: true }, "");
        entreesHistorique.current++;
      } catch {
        /* navigateur sans historique : le bouton de l'écran suffit */
      }
    }
    vueRef.current = v;
    setVueEtat(v);
  }, []);
  /** Changer d'écran sans rien empiler (écran de passage). */
  const remplacer = useCallback((v: Vue): void => {
    vueRef.current = v;
    setVueEtat(v);
  }, []);
  const depiler = useCallback((): void => {
    let v = pile.current.pop();
    while (v !== undefined && !retournable.current(v)) v = pile.current.pop();
    const cible = v ?? "proposition";
    vueRef.current = cible;
    setVueEtat(cible);
  }, []);
  /** Un retour du téléphone qui a un sens particulier sur l'écran du moment (l'entrée dans une tribu). */
  const retourSpecial = useRef<(() => void) | null>(null);
  useEffect(() => {
    const surRetour = (): void => {
      // L'arrivée d'un lien de tribu (#/s/…) dans l'onglet ouvert déclenche
      // aussi « popstate » : ce n'est pas un retour, le lien s'en occupe
      // (sans cela, on revenait à la proposition — ou, déjà dans la tribu,
      // la liste des tribus s'ouvrait toute seule).
      if (/^#\/s\//.test(location.hash)) return;
      entreesHistorique.current = Math.max(0, entreesHistorique.current - 1);
      if (retourSpecial.current) {
        retourSpecial.current();
        return;
      }
      depiler();
    };
    addEventListener("popstate", surRetour);
    return () => removeEventListener("popstate", surRetour);
  }, [depiler]);
  /** Le bouton « retour » de chaque écran. */
  const retour = useCallback((): void => {
    if (entreesHistorique.current > 0) history.back();
    else depiler();
  }, [depiler]);
  const [secteur, setSecteur] = useState<Secteur | null>(null);
  const [secteurs, setSecteurs] = useState<SecteurCompte[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [fix, setFix] = useState<Fix | null>(null);
  const [pseudo, setPseudoEtat] = useState<string>(() => lire(CLE_PSEUDO) ?? props.account?.pseudo ?? "");
  const [soirees, setSoirees] = useState<SoireePassee[]>(lireSoirees);
  const [recap, setRecap] = useState<SoireePassee | null>(null);
  /** Les événements où l'on peut entrer (par la position, ou par trois appuis sur Hoa). */
  const [evenements, setEvenements] = useState<EvenementPublic[]>([]);
  const [evenementId, setEvenementId] = useState<string | null>(() => evenementCourant()?.id ?? null);
  /** Le profil du compte (points, soirées, badges, boutique) et une éventuelle sanction. */
  const { profil, sanction } = useProfil();
  /** Les sessions de la ville : Hoa est-il réveillé ? */
  const [planning, setPlanning] = useState<Planning | null>(null);
  /** Une soirée passée dont on relit le chat. */
  const [relue, setRelue] = useState<SoireePassee | null>(null);
  /** « Signaler » hors tribu : une soirée passée, ses membres, un motif. */
  const [signalSalle, setSignalSalle] = useState<{ depart: SoireePassee | null } | null>(null);
  const [envoiSignal, setEnvoiSignal] = useState<{ en: boolean; erreur: string | null; fait: boolean }>({ en: false, erreur: null, fait: false });
  /** « Tes soirées », la feuille de Mon compte. */
  const [feuilleSoirees, setFeuilleSoirees] = useState(false);
  const [envoiPerso, setEnvoiPerso] = useState<{ en: boolean; erreur: string | null }>({ en: false, erreur: null });
  const photo = usePhoto();
  /** La fin de soirée (H-Garder) : ce que la soirée a rapporté, et les badges gagnés. */
  const [garde, setGarde] = useState<{ gain: number | null; nouveaux: Badge[] } | null>(null);
  const badgesAvant = useRef<string[]>([]);
  const [envoiBoutique, setEnvoiBoutique] = useState<{ en: boolean; erreur: string | null }>({ en: false, erreur: null });

  /* ---------------- Ce qui se pose par-dessus ---------------- */
  const [menu, setMenu] = useState(false);
  const [feuilleArr, setFeuilleArr] = useState<{ message?: string } | null>(null);
  const [signaler, setSignaler] = useState(false);

  /* ---------------- Tribu ---------------- */
  const socket = useRef<SessionSocket | null>(null);
  const [sessionJointe, setSessionJointe] = useState<SessionSummary | null>(null);
  const [experience, setExperience] = useState<Experience | null>(null);
  const [pid, setPid] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("entered");
  const [tribe, setTribe] = useState<TribePrivate | null>(null);
  const [chat, setChat] = useState<LigneChat[]>([]);
  const [repondues, setRepondues] = useState<string[]>([]);
  const [points, setPoints] = useState<PointsCommuns | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [erreurTribu, setErreurTribu] = useState<string | null>(null);
  const [erreurPseudo, setErreurPseudo] = useState<string | null>(null);
  const [besoinPseudo, setBesoinPseudo] = useState(false);
  /** La présentation revue depuis Paramètres (refermable, sans effet sur la tribu en cours). */
  const [introRejouee, setIntroRejouee] = useState(false);
  const [annulee, setAnnulee] = useState<{ presents: number; minimum: number } | null>(null);
  const [fin, setFin] = useState<{ reunis: boolean } | null>(null);
  const [rejointA, setRejointA] = useState<number | null>(null);
  const pseudoTente = useRef(false);
  /** Arrivé au rendez-vous à un moment de la soirée : la fin et l'historique le disent. */
  const aEteReuni = useRef(false);
  if (stage === "gathered") aEteReuni.current = true;
  // Ce que l'historique garde de la soirée, lu au moment où elle se termine.
  const tribeRef = useRef<TribePrivate | null>(null);
  tribeRef.current = tribe;
  const pointsRef = useRef<PointsCommuns | null>(null);
  pointsRef.current = points;
  const rejointRef = useRef<number | null>(null);
  rejointRef.current = rejointA;
  const chatRef = useRef<LigneChat[]>([]);
  chatRef.current = chat;
  const pidRef = useRef<string | null>(null);
  pidRef.current = pid;
  const sessionRef = useRef<string | null>(null);
  sessionRef.current = sessionJointe?.id ?? null;

  /* ---------------- Quitter, annulable 5 s ---------------- */
  const [depart, setDepart] = useState<{ jusqua: number; timer: number } | null>(null);

  const enTribu = tribe !== null && !fin && !annulee && socket.current !== null;
  const modeDemo = useModeDemo(sessionJointe?.id.startsWith("evt-") ?? false);

  /* ---------------- Chargements ---------------- */

  const chargerSessions = useCallback(async (s: Secteur): Promise<SessionSummary[]> => {
    void json<Planning>("/api/planning").then(setPlanning, () => undefined);
    try {
      if (s.id === REGIE.id) {
        const r = await json<{ sessions: SessionSummary[] }>("/api/secteur-regie");
        setSessions(r.sessions);
        return r.sessions;
      }
      const r = await json<{ sessions: SessionSummary[]; entreeFermee?: boolean }>(
        `/api/sessions?sectorId=${encodeURIComponent(s.id)}`,
      );
      setSessions(r.sessions);
      if (r.entreeFermee) setMessage("Les entrées sont fermées pour ce soir. Les tribus en cours finissent leur soirée.");
      return r.sessions;
    } catch {
      setMessage("Le serveur ne répond pas. Réessaie dans un instant.");
      return [];
    }
  }, []);

  /** Les arrondissements, chacun avec son nombre de tribus ouvertes. */
  const chargerSecteurs = useCallback(async (): Promise<void> => {
    try {
      const [a, b] = await Promise.all([
        json<{ sectors: Array<Secteur & { publicSector: boolean }> }>("/api/sectors"),
        json<{ sessions: SessionSummary[] }>("/api/sessions"),
      ]);
      const compte = new Map<string, number>();
      for (const s of b.sessions) compte.set(s.sectorId, (compte.get(s.sectorId) ?? 0) + 1);
      setSecteurs(
        a.sectors.filter((x) => x.publicSector).map((x) => ({ id: x.id, name: x.name, tribus: compte.get(x.id) ?? 0 })),
      );
    } catch {
      setMessage("Le serveur ne répond pas. Réessaie dans un instant.");
    }
  }, []);

  const ouvrirArrondissements = useCallback(
    (msg?: string): void => {
      setMenu(false);
      setFeuilleArr({ message: msg });
      void chargerSecteurs();
    },
    [chargerSecteurs],
  );

  const choisirSecteur = useCallback(
    async (s: Secteur, versListe = false): Promise<void> => {
      setSecteur(s);
      setFeuilleArr(null);
      if (s.id !== REGIE.id) ecrire(CLE_SECTEUR, JSON.stringify({ id: s.id, name: s.name }));
      setMessage(null);
      await chargerSessions(s);
      setVue(versListe ? "autres" : "proposition");
    },
    [chargerSessions],
  );

  /** Entrer dans un événement : sa voix remplace celle de Hoa. */
  const montrerEvenements = useCallback((liste: EvenementPublic[], id?: string): void => {
    const e = liste.find((x) => x.id === id) ?? liste[0];
    if (!e) return;
    setEvenements(liste);
    setEvenementId(e.id);
    if (evenementCourant()?.id !== e.id) {
      // Une annonce de la ville ne se dit pas avec la voix de l'organisateur :
      // on la range, et on lit celle de l'événement.
      fermerAnnonce();
      void json<{ annonce: { texte: string; a: number } | null }>(`/api/annonce?evt=${encodeURIComponent(e.id)}`)
        .then((r) => r.annonce && annonceDuServeur(r.annonce.a, r.annonce.texte))
        .catch(() => undefined);
    }
    poserEvenement({ id: e.id, nom: e.nom, voix: e.voix });
    setMenu(false);
    setFeuilleArr(null);
    setMessage(null);
    setVue("evenement");
  }, []);

  const quitterEvenement = useCallback((): void => {
    if (evenementCourant()) fermerAnnonce();
    poserEvenement(null);
    setEvenementId(null);
    setEvenements([]);
  }, []);

  const localiser = useCallback(async (): Promise<void> => {
    try {
      const f = await currentFix();
      ecrire("position", "autorisee");
      setFix(f);
      // Dans la zone d'un événement ouvert : c'est lui qu'on propose.
      const proches = await evenementsProches(f);
      if (proches.length > 0) {
        montrerEvenements(proches, evenementCourant()?.id);
        return;
      }
      const r = await json<{ sector: Secteur | null; sessions: SessionSummary[] }>("/api/locate", {
        method: "POST",
        body: JSON.stringify({ fix: f }),
      });
      if (!r.sector) {
        setVue("proposition");
        ouvrirArrondissements("Tu es hors des arrondissements où je réunis des tribus. Choisis-en un.");
        return;
      }
      setSecteur(r.sector);
      ecrire(CLE_SECTEUR, JSON.stringify({ id: r.sector.id, name: r.sector.name }));
      setSessions(r.sessions);
      setMessage(null);
      setFeuilleArr(null);
      setVue("proposition");
    } catch {
      ecrire("position", null);
      setVue("proposition");
      ouvrirArrondissements("Pas de position, pas de souci. Où es-tu ce soir ?");
    }
  }, [ouvrirArrondissements, montrerEvenements]);

  /** Relire l'événement où l'on est entré ; s'il est fini, Hoa reprend la parole. */
  const revenirALEvenement = useCallback(async (): Promise<boolean> => {
    const courant = evenementCourant();
    if (!courant) return false;
    try {
      const r = await json<{ evenement: EvenementPublic }>(`/api/evenements/${encodeURIComponent(courant.id)}`);
      montrerEvenements([r.evenement, ...evenements.filter((x) => x.id !== courant.id)], courant.id);
      return true;
    } catch {
      quitterEvenement();
      return false;
    }
  }, [evenements, montrerEvenements, quitterEvenement]);

  /** Là où l'on revient quand on n'est dans aucune tribu. */
  const accueil = useCallback((): void => {
    // Espaces Humains : pas de quartier. L'événement de l'appareil (QR), sinon
    // les événements ouverts, sinon l'invitation à scanner le QR du stand.
    if (ESPACES_HUMAINS) {
      const ouverts = (): void => {
        void json<{ evenements: EvenementPublic[] }>("/api/evenements/ouverts")
          .then((r) => (r.evenements.length > 0 ? montrerEvenements(r.evenements) : setVue("evenement")))
          .catch(() => setVue("evenement"));
      };
      if (evenementCourant()) {
        void revenirALEvenement().then((ok) => {
          if (!ok) ouverts();
        });
      } else ouverts();
      return;
    }
    const quartier = (): void => {
      const connu = lire(CLE_SECTEUR);
      if (connu) {
        try {
          void choisirSecteur(JSON.parse(connu) as Secteur);
          return;
        } catch {
          /* valeur illisible : on repart de la position */
        }
      }
      void localiser();
    };
    const suite = (): void => {
      // Position déjà accordée : on regarde en passant si l'on est dans la
      // zone d'un événement ; sinon, le quartier habituel, comme avant.
      if (lire("position") === "autorisee" && lire(CLE_SECTEUR)) {
        quartier();
        void currentFix()
          .then(evenementsProches)
          .then((l) => {
            // Entré dans une tribu entre-temps : on ne l'en sort pas.
            if (l.length > 0 && !tribeRef.current) montrerEvenements(l);
          })
          .catch(() => undefined);
        return;
      }
      quartier();
    };
    if (evenementCourant()) {
      void revenirALEvenement().then((ok) => {
        if (!ok) suite();
      });
      return;
    }
    suite();
  }, [choisirSecteur, localiser, montrerEvenements, revenirALEvenement]);

  // Trois appuis sur la tête de Hoa : les événements ouverts, sans condition
  // de position (l'équipe d'un événement, les essais). S'il n'y en a aucun,
  // les salles d'essai de la régie.
  const tripleEnTribu = useRef<(() => void) | null>(null);
  useEffect(() => {
    ecouterTripleHoa(() => {
      setMenu(false);
      // Dans une tribu, la tête de Hoa ouvre la liste de sa tribu, pas un autre lieu.
      if (tripleEnTribu.current) {
        tripleEnTribu.current();
        return;
      }
      void json<{ evenements: EvenementPublic[] }>("/api/evenements/ouverts")
        .then((r) => {
          if (r.evenements.length > 0) montrerEvenements(r.evenements, evenementCourant()?.id);
          else void choisirSecteur(REGIE, true);
        })
        .catch(() => void choisirSecteur(REGIE, true));
    });
    return () => ecouterTripleHoa(null);
  }, [choisirSecteur, montrerEvenements]);

  // Dans un événement, la liste des tables vit, et le téléphone se compte
  // présent dans la zone (si la position est accordée).
  useEffect(() => {
    if ((vue !== "evenement" && vue !== "tribus") || !evenementId) return;
    const id = window.setInterval(() => {
      void (async () => {
        let liste: EvenementPublic[] = [];
        if (lire("position") === "autorisee") {
          try {
            liste = await evenementsProches(await currentFix());
          } catch {
            /* pas de position ce coup-ci */
          }
        }
        if (!liste.some((x) => x.id === evenementId)) {
          try {
            const r = await json<{ evenement: EvenementPublic }>(`/api/evenements/${encodeURIComponent(evenementId)}`);
            liste = [r.evenement, ...liste.filter((x) => x.id !== evenementId)];
          } catch {
            return;
          }
        }
        setEvenements(liste);
      })();
    }, 20_000);
    return () => window.clearInterval(id);
  }, [vue, evenementId]);

  // La liste vit : on la relit toutes les dix secondes tant qu'on la regarde.
  useEffect(() => {
    if (!secteur || (vue !== "proposition" && vue !== "autres" && vue !== "quitte" && vue !== "tribus")) return;
    const id = window.setInterval(() => void chargerSessions(secteur), 10_000);
    return () => window.clearInterval(id);
  }, [secteur, vue, chargerSessions]);

  /* ---------------- Tribu : connexion et messages ---------------- */

  const remettreAZero = useCallback((): void => {
    socket.current?.close();
    socket.current = null;
    setSessionJointe(null);
    setExperience(null);
    setPid(null);
    setStage("entered");
    setTribe(null);
    setChat([]);
    setRepondues([]);
    setPoints(null);
    setDistance(null);
    setErreurTribu(null);
    setErreurPseudo(null);
    setBesoinPseudo(false);
    setAnnulee(null);
    setFin(null);
    setRejointA(null);
    setSignaler(false);
    taireHoa();
    pseudoTente.current = false;
    aEteReuni.current = false;
  }, []);

  /** La soirée qui se termine entre dans l'historique de ce téléphone. */
  const archiver = useCallback((): void => {
    const t = tribeRef.current;
    if (!t) return;
    garderSoiree({
      // L'identifiant de la session : c'est celui que le compte garde aussi.
      id: sessionRef.current ?? t.id,
      titre: t.title,
      rdvAt: t.rdvAt,
      rejointA: rejointRef.current ?? t.chatOpensAt,
      lieu: t.rendezvous.name,
      personnes: aEteReuni.current ? Math.max(1, t.arrives) : t.members.length,
      reunis: aEteReuni.current,
      avec: t.members.filter((m) => !m.moi && m.arrive).map((m) => m.pseudo),
      pointCommun: pointsRef.current?.answerText ?? null,
      chat: chatRef.current.slice(-200),
      moi: pidRef.current,
      membres: t.members.filter((m) => !m.moi).map((m) => ({ pid: m.pid, pseudo: m.pseudo })),
    });
    setSoirees(lireSoirees());
  }, []);

  const onMessage = useCallback(
    (msg: ServerMessage): void => {
      switch (msg.type) {
        case "welcome": {
          setPid(msg.pid);
          setSessionJointe(msg.session);
          setExperience(msg.experience as Experience);
          setStage(msg.stage);
          ecrireTribuEnCours({ sessionId: msg.session.id, nom: msg.session.subject ?? msg.session.name, avecCompte: msg.authenticated });
          if (msg.pseudo) pseudoTente.current = true;
          return;
        }
        case "stage":
          setStage(msg.stage);
          if (msg.stage === "joined" || msg.stage === "chatting" || msg.stage === "rendezvous") {
            setRejointA((r) => r ?? serverNow());
          }
          return;
        case "pseudo":
          setBesoinPseudo(false);
          setErreurPseudo(null);
          return;
        case "tribe": {
          // HOA pendant la soirée : qui arrive dans la tribu, qui est sur place.
          const avant = tribeRef.current;
          if (avant && msg.tribe && avant.id === msg.tribe.id) {
            const connus = new Set(avant.members.map((m) => m.pseudo));
            const nouveaux = msg.tribe.members.filter((m) => !m.moi && !connus.has(m.pseudo));
            const deja = new Set(avant.members.filter((m) => m.arrive).map((m) => m.pseudo));
            const arrives = msg.tribe.members.filter((m) => !m.moi && m.arrive && !deja.has(m.pseudo));
            if (arrives.length) {
              const texte = `${arrives.map((m) => m.pseudo).join(" et ")} ${arrives.length > 1 ? "sont" : "est"} sur place.`;
              direHoa(texte);
              prevenirArrivee(texte);
              const a = serverNow();
              setChat((c) => [
                ...c.slice(-199),
                ...arrives
                  .filter((m) => !c.some((x) => x.id === `arrivee-${m.pid}`))
                  .map((m) => ({ id: `arrivee-${m.pid}`, pid: "", pseudo: "Hoa", text: `${m.pseudo} est sur place.`, at: a, hoa: true })),
              ]);
            } else if (nouveaux.length) direHoa(`${nouveaux.map((m) => m.pseudo).join(" et ")} ${nouveaux.length > 1 ? "rejoignent" : "rejoint"} la ${sessionRef.current?.startsWith("evt-") ? "table" : "tribu"}.`, 5000, "sourire");
            if (avant.phase !== "reunie" && msg.tribe.phase === "reunie") notifier("part", sessionRef.current?.startsWith("evt-") ? "Vous êtes réunis." : "Vous êtes réunis. HOA est libre !");
          }
          setTribe(msg.tribe);
          return;
        }
        case "chat":
          setChat((c) => (c.some((x) => x.id === msg.id) ? c : [...c.slice(-199), msg]));
          if (msg.pid !== pidRef.current) bip();
          return;
        case "waiting_answers":
          setRepondues(msg.answered);
          return;
        case "approach":
          setDistance(msg.distanceM);
          return;
        case "reveal":
          setPoints({ questionText: msg.questionText, answerText: msg.answerText, avec: msg.avec, autres: msg.autres });
          return;
        case "cancelled":
          setAnnulee({ presents: msg.presents, minimum: msg.minimum });
          oublierTribuEnCours();
          // Où que l'on soit (Mon compte, la liste…), la fin de la tribu se montre.
          remplacer("tribu");
          notifier("annulee", "Ta tribu est annulée : pas assez de monde ce soir.");
          return;
        case "released":
          taireHoa();
          archiver();
          setFin({ reunis: aEteReuni.current });
          setGarde({ gain: null, nouveaux: [] });
          oublierTribuEnCours();
          remplacer("tribu");
          if (msg.receipt) void addReceipt(msg.receipt as Parameters<typeof addReceipt>[0]).then(props.onReceipt);
          return;
        case "closed":
          oublierTribuEnCours();
          setFin((f) => f ?? { reunis: aEteReuni.current });
          remplacer("tribu");
          return;
        case "event":
          if (msg.kind === "regie") annonceDuServeur(msg.at, msg.text);
          if (msg.kind === "chat_open") notifier("part", "Ta tribu part : le chat est ouvert.");
          return;
        case "error":
          if (msg.code.startsWith("pseudo:")) {
            setBesoinPseudo(true);
            setErreurPseudo(msg.message);
            return;
          }
          if (ERREURS_FATALES.includes(msg.code)) {
            taireHoa();
            oublierTribuEnCours();
            socket.current?.close();
            socket.current = null;
            setTribe(null);
            setMessage(msg.message);
            setVue("proposition");
            // Une suspension a son écran (H-Suspendu) : on relit le compte pour l'avoir.
            if ((msg.code === "suspendu" || msg.code === "banni") && props.account) void verifierCompte(props.account);
            return;
          }
          if (msg.code === "position:saut" || msg.code === "transition") return;
          setErreurTribu(msg.message);
          return;
        default:
          return;
      }
    },
    [props.onReceipt, archiver, remplacer],
  );

  const rejoindre = useCallback(
    (sessionId: string): void => {
      // Une seule tribu à la fois : en rejoindre une autre, c'est quitter la sienne.
      if (socket.current) socket.current.send({ type: "leave" });
      oublierTribuEnCours();
      remettreAZero();
      demanderNotifications();
      setMessage(null);
      setMenu(false);
      setFeuilleArr(null);
      const s = new SessionSocket(
        sessionId,
        { onMessage: (m) => onMessage(m) },
        props.account?.account ?? null,
        "participant",
        props.account?.receipts ?? [],
      );
      socket.current = s;
      s.connect();
      setVue("tribu");
    },
    [onMessage, props.account, remettreAZero],
  );

  // Le pseudo part tout seul quand on le connaît déjà.
  useEffect(() => {
    if (stage !== "in_session" || pseudoTente.current) return;
    if (pseudo.trim().length >= 2) {
      pseudoTente.current = true;
      socket.current?.send({ type: "set_pseudo", pseudo: pseudo.trim() });
    } else {
      setBesoinPseudo(true);
    }
  }, [stage, pseudo]);

  // Les erreurs de tribu s'effacent seules.
  useEffect(() => {
    if (!erreurTribu) return;
    const id = window.setTimeout(() => setErreurTribu(null), 6000);
    return () => window.clearTimeout(id);
  }, [erreurTribu]);

  // Position : pendant le chat (on peut déjà marcher) et le rendez-vous.
  const surPlace = tribe?.rendezvous.surPlace === true;
  useEffect(() => {
    if ((stage !== "chatting" && stage !== "rendezvous") || surPlace) return;
    let relacher: (() => void) | null = null;
    if (stage === "rendezvous") void keepAwake().then((r) => (relacher = r));
    const stop = watchFix(
      (f) => {
        setFix(f);
        socket.current?.send({ type: "position", fix: f });
      },
      () => {},
    );
    return () => {
      stop();
      relacher?.();
    };
  }, [stage, surPlace]);

  useEffect(() => () => socket.current?.close(), []);

  /* ---------------- Démarrage ---------------- */

  const demarre = useRef(false);
  useEffect(() => {
    if (demarre.current) return;
    demarre.current = true;
    const connu = lire(CLE_SECTEUR);
    if (connu) {
      try {
        setSecteur(JSON.parse(connu) as Secteur);
      } catch {
        /* illisible */
      }
    }
    const enCours = lireTribuEnCours();
    if (enCours) {
      rejoindre(enCours.sessionId);
      return;
    }
    // Espaces Humains : pas de présentation de Hoa, on entre par le QR du stand.
    if (!lire(CLE_INTRO) && !ESPACES_HUMAINS) {
      remplacer("intro");
      return;
    }
    if (props.sessionId) {
      rejoindre(consommerLien() ?? props.sessionId);
      return;
    }
    accueil();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accueil, rejoindre, props.sessionId]);

  // Un lien de salle ouvert pendant que l'application tourne déjà (QR code,
  // lien partagé) : on y entre sans recharger la page.
  const derniereSalle = useRef(props.sessionId);
  /** La salle d'un lien (QR code) : on y entre une fois, puis l'adresse redevient neutre. */
  const salleDuLien = useRef(props.sessionId ?? null);
  const consommerLien = (): string | null => {
    const id = salleDuLien.current;
    salleDuLien.current = null;
    if (id && /^#\/s\//.test(location.hash)) history.replaceState(history.state, "", location.pathname);
    return id;
  };
  useEffect(() => {
    if (!props.sessionId || props.sessionId === derniereSalle.current) return;
    derniereSalle.current = props.sessionId;
    salleDuLien.current = props.sessionId;
    if (lire(CLE_INTRO) || ESPACES_HUMAINS) rejoindre(consommerLien() ?? props.sessionId);
  }, [props.sessionId, rejoindre]);

  // L'ancienne adresse « #/compte » : Paramètres, là où vit le compte.
  useEffect(() => {
    if (props.ouvrir !== "parametres" || vue === "chargement" || vue === "intro") return;
    setVue("parametres");
    history.replaceState(null, "", location.pathname);
    // Une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ouvrir, vue === "chargement"]);

  const finIntro = (suite: () => void): void => {
    ecrire(CLE_INTRO, "1");
    const lien = consommerLien();
    if (lien) {
      rejoindre(lien);
      return;
    }
    suite();
  };

  /* ---------------- Actions ---------------- */

  /** Le secteur d'où vient la tribu, pour « retour » et « annulée ». */
  const secteurDeLaTribu = (): Secteur | null => {
    if (!sessionJointe) return secteur;
    if (sessionJointe.sectorId === REGIE.id) return REGIE;
    return { id: sessionJointe.sectorId, name: sessionJointe.sectorName };
  };

  const dansUnEvenement = (): boolean => (sessionJointe?.id.startsWith("evt-") ?? false) && evenementCourant() !== null;

  const quitter = (): void => {
    if (depart) return;
    // Une table d'événement : on la quitte et on revient aux tables.
    if (dansUnEvenement()) {
      socket.current?.send({ type: "leave" });
      oublierTribuEnCours();
      remettreAZero();
      void revenirALEvenement();
      return;
    }
    const timer = window.setTimeout(() => {
      socket.current?.send({ type: "leave" });
      oublierTribuEnCours();
      remettreAZero();
      setDepart(null);
      // Revenu entre-temps sur la tribu : elle n'existe plus, on montre la liste.
      if (vueRef.current === "tribu" || vueRef.current === "tribus") remplacer("autres");
    }, 5000);
    setDepart({ jusqua: Date.now() + 5000, timer });
    const s = secteurDeLaTribu();
    if (s) {
      setSecteur(s);
      void chargerSessions(s);
    }
    setVue("quitte");
  };

  const annulerDepart = (): void => {
    if (!depart) return;
    window.clearTimeout(depart.timer);
    setDepart(null);
    retour();
  };

  /** Choisir une tribu : un départ en cours devient définitif, l'ancienne tribu est quittée. */
  const choisirTribu = (id: string): void => {
    if (depart) {
      window.clearTimeout(depart.timer);
      setDepart(null);
    }
    if (tribe && sessionJointe?.id === id && !fin && !annulee) {
      setVue("tribu");
      return;
    }
    rejoindre(id);
  };

  const [envoiCreation, setEnvoiCreation] = useState(false);
  const [erreurCreation, setErreurCreation] = useState<string | null>(null);
  const creer = async (c: { sujetId: string; lieu: number; rdvAt: number; min: number; max: number }): Promise<void> => {
    if (!secteur) return;
    setEnvoiCreation(true);
    setErreurCreation(null);
    try {
      // Ouvrir une tribu demande un compte avec un e-mail (voir CompteRequis).
      const compte = props.account;
      if (!compte?.email) throw new Error("Ouvrir une tribu demande un compte avec un e-mail.");
      const { nonce } = await json<{ nonce: string }>("/api/nonce");
      const startInMin = Math.floor((c.rdvAt - serverNow()) / 60_000);
      const signe = JSON.stringify(["creer-tribu", nonce, secteur.id, c.sujetId, c.lieu, startInMin, c.min, c.max]);
      const r = await json<{ id: string }>("/api/tribus", {
        method: "POST",
        body: JSON.stringify({
          pubkey: compte.account.publicKeyHex,
          nonce,
          signature: signMessage(compte.account, signe),
          sectorId: secteur.id,
          sujetId: c.sujetId,
          placeIndex: c.lieu,
          startInMin,
          min: c.min,
          max: c.max,
        }),
      });
      setEnvoiCreation(false);
      rejoindre(r.id);
    } catch (e) {
      setErreurCreation(e instanceof Error ? e.message : "La tribu n'a pas pu être créée.");
      setEnvoiCreation(false);
    }
  };

  const changerPseudo = (p: string): void => {
    if (p.length < 2) return;
    setPseudoEtat(p);
    ecrire(CLE_PSEUDO, p);
    if (props.account) void enregistrerPseudoCompte(p);
    if (props.account?.email) void pseudoDuCompte(props.account, p);
  };

  /* ---------------- Compte (e-mail + mot de passe) ---------------- */

  const [envoiCompte, setEnvoiCompte] = useState<Envoi>({ en: false, erreur: null });
  const [cleMontree, setCleMontree] = useState<{ cle: string; nouvelle: boolean } | null>(null);
  const [suppression, setSuppression] = useState(false);
  const [effacement, setEffacement] = useState(false);

  /** Le compte : une fois créé ou retrouvé, « retour » ramène à l'écran qui l'a demandé. */
  const versCompte = (v: "compte" | "connexion" | "secours"): void => {
    setEnvoiCompte({ en: false, erreur: null });
    setVue(v);
  };

  const agirCompte = async (f: () => Promise<void>): Promise<void> => {
    setEnvoiCompte({ en: true, erreur: null });
    try {
      await f();
      setEnvoiCompte({ en: false, erreur: null });
    } catch (e) {
      setEnvoiCompte({ en: false, erreur: e instanceof Error ? e.message : "Ça n'a pas marché. Réessaie dans un instant." });
    }
  };

  const retrouve = (compte: LocalAccount, pseudoServeur: string): void => {
    props.onAccount(compte);
    if (!pseudo && pseudoServeur) changerPseudo(pseudoServeur);
  };

  // Un compte supprimé ou délié depuis un autre téléphone ne l'est plus ici.
  const compteAVerifier = props.account?.email ? props.account : null;
  useEffect(() => {
    if (!compteAVerifier) return;
    void verifierCompte(compteAVerifier).then(async (c) => {
      if (c !== null) return;
      await enregistrerEmail(null);
      const a = await loadAccount();
      if (a) props.onAccount(a);
    });
    // Une fois par compte relié.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compteAVerifier?.account.publicKeyHex, compteAVerifier?.email]);

  // Sans compte à e-mail, l'appareil peut quand même avoir reçu une décision
  // de la régie (avertissement, suspension) : on la lit une fois.
  const appareil = props.account && !props.account.email ? props.account : null;
  useEffect(() => {
    if (appareil) void verifierCompte(appareil);
    // Une fois par appareil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appareil?.account.publicKeyHex]);

  const ouvrirSignalSalle = (depart: SoireePassee | null): void => {
    setMenu(false);
    setEnvoiSignal({ en: false, erreur: null, fait: false });
    setSignalSalle({ depart });
  };

  // La photo de profil part vers la tribu dès qu'on y a une place, et à chaque changement.
  const photoEnvoyee = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!pid || !socket.current) {
      photoEnvoyee.current = undefined;
      return;
    }
    if (photoEnvoyee.current === photo) return;
    if (photoEnvoyee.current === undefined && !photo) {
      photoEnvoyee.current = null;
      return;
    }
    photoEnvoyee.current = photo;
    socket.current.send({ type: "set_photo", photo });
  }, [pid, photo]);

  // Fin de soirée avec un compte : le serveur a noté la soirée ; on relit
  // le profil pour dire ce qu'elle a rapporté, et les badges gagnés.
  useEffect(() => {
    if (!garde || garde.gain !== null || !props.account?.email) return;
    const compte = props.account;
    const id = sessionJointe?.id;
    const t = window.setTimeout(() => {
      void verifierCompte(compte).then(() => {
        const p = profilCourant();
        const gain = p?.soirees.find((x) => x.id === id)?.gain ?? 0;
        const nouveaux = (p?.badges ?? []).filter((b) => b.obtenu && !badgesAvant.current.includes(b.id));
        setGarde({ gain, nouveaux });
      });
    }, 600);
    return () => window.clearTimeout(t);
  }, [garde, props.account, sessionJointe?.id]);

  // Les badges déjà obtenus, pour reconnaître les nouveaux en fin de soirée.
  useEffect(() => {
    if (profil && !fin) badgesAvant.current = profil.badges.filter((b) => b.obtenu).map((b) => b.id);
  }, [profil, fin]);

  // « Préviens-moi » : tant que l'application est ouverte, Hoa réveille une
  // demi-heure avant la session (l'agenda s'en charge application fermée).
  useEffect(() => {
    const s = lireSessionPrevenue();
    if (!s) return;
    const dans = s.debut - 30 * 60_000 - Date.now();
    if (dans < 0 || dans > 24 * 3_600_000) return;
    const t = window.setTimeout(() => {
      notifier("session", "Les tribus partent dans une demi-heure.");
      direHoa("Les tribus partent dans une demi-heure. On y va ?", 9000, "clin");
      ecrireSessionPrevenue(null);
    }, dans);
    return () => window.clearTimeout(t);
  }, [vue]);

  // Plus que deux minutes de chat : Hoa le dit.
  const finChat = tribe ? (tribe.chatFinA === undefined ? tribe.rdvAt : tribe.chatFinA) : null;
  useEffect(() => {
    if (!tribe || tribe.phase !== "chat" || finChat === null) return;
    const dans = finChat - 120_000 - serverNow();
    if (dans < 0) return;
    const t = window.setTimeout(() => direHoa(sessionRef.current?.startsWith("evt-") ? "Plus que deux minutes avant le rendez-vous à la table." : "Plus que deux minutes de chat. Dites-vous comment vous reconnaître."), dans);
    return () => window.clearTimeout(t);
  }, [tribe?.phase, finChat, tribe]);

  const ouvrir = (v: Vue): void => {
    setMenu(false);
    if (v === "moi") setFeuilleSoirees(false);
    setVue(v);
  };

  // Les écrans où « retour » peut encore ramener.
  retournable.current = (v: Vue): boolean => {
    switch (v) {
      case "chargement":
      case "intro":
      case "quitte":
      case "cle":
      case "suspendu":
        return false;
      case "tribu":
        // La fin d'une tribu (réunie, annulée) reste à l'écran tant qu'on ne l'a pas fermée.
        return (enTribu && !depart) || ((fin !== null || annulee !== null) && sessionJointe !== null);
      case "tribus":
        return enTribu && !depart;
      case "recap":
        return recap !== null;
      case "relire":
        return relue !== null;
      case "prevenu":
        return Boolean(planning?.prochaine);
      case "evenement":
        return evenements.length > 0;
      case "compte":
      case "connexion":
      case "secours":
        return !props.account?.email;
      default:
        return true;
    }
  };

  /* ---------------- Rendu ---------------- */

  const initiale = (pseudo.trim()[0] ?? "").toUpperCase();
  // Avec un compte, les points du serveur font foi ; sans compte, ceux du téléphone.
  const nPoints = profil ? profil.points : totalPoints(soirees);
  /* Les soirées de « Moi » : celles du compte (tous les téléphones), enrichies
   * de ce que ce téléphone garde (avec qui, point commun, chat). */
  const soireesAffichees: SoireePassee[] = (() => {
    if (!profil) return soirees;
    const locales = new Map(soirees.map((x) => [x.id, x]));
    const vues = new Set<string>();
    const out: SoireePassee[] = profil.soirees.map((x) => {
      vues.add(x.id);
      const l = locales.get(x.id);
      return l
        ? { ...l, gain: x.gain }
        : { id: x.id, titre: x.titre, rdvAt: x.rdvAt, rejointA: x.rdvAt, lieu: x.lieu, personnes: x.personnes, reunis: x.reunis, avec: [], pointCommun: null, gain: x.gain };
    });
    for (const l of soirees) if (!vues.has(l.id)) out.push({ ...l, gain: 0 });
    return out.sort((a, b) => b.rdvAt - a.rdvAt);
  })();
  const voirPoints = (): void => ouvrir("moi");

  // Le menu « Moi » : Mon compte, Autres quartiers, Paramètres, Signaler.
  // Espaces Humains : ni compte, ni quartiers ; les réglages d'affichage seulement.
  const entrees: EntreeMenu[] = ESPACES_HUMAINS
    ? [{ libelle: "Paramètres", onClick: () => ouvrir("parametres") }]
    : [
        { libelle: "Mon compte", onClick: () => ouvrir("moi") },
        { libelle: "Autres quartiers", onClick: () => ouvrirArrondissements() },
        { libelle: "Paramètres", onClick: () => ouvrir("parametres") },
      ];
  if ((vue === "tribu" || vue === "tribus") && enTribu) {
    entrees.push({
      libelle: "Signaler",
      onClick: () => {
        setMenu(false);
        setSignaler(true);
      },
    });
  }

  const feuilleSignal = signalSalle ? (
    <FeuilleSignalerSalle
      soirees={soireesAffichees}
      maintenant={maintenant}
      depart={signalSalle.depart}
      envoi={envoiSignal.en}
      erreur={envoiSignal.erreur}
      fait={envoiSignal.fait}
      onFermer={() => setSignalSalle(null)}
      onEnvoyer={(e) => {
        setEnvoiSignal({ en: true, erreur: null, fait: false });
        void signalerSalle(e).then(
          () => setEnvoiSignal({ en: false, erreur: null, fait: true }),
          (x: Error) => setEnvoiSignal({ en: false, erreur: x.message, fait: false }),
        );
      }}
    />
  ) : null;

  const dessus = (
    <>
      {feuilleSignal}
      {menu ? <MenuMoi entrees={entrees} onFermer={() => setMenu(false)} /> : null}
      {feuilleArr ? (
        <FeuilleArrondissements
          secteurs={secteurs}
          actuel={secteur?.id ?? null}
          message={feuilleArr.message}
          onChoisir={(s) => void choisirSecteur(s, enTribu)}
          onFermer={secteur ? () => setFeuilleArr(null) : undefined}
        />
      ) : null}
    </>
  );

  /**
   * Le retour de l'accueil : l'écran d'avant s'il y en a un ; sinon l'étape
   * d'avant l'accueil — le choix d'arrondissement, ou la ville quand on est
   * dans l'espace régie.
   */
  const retourAccueil = (): void => {
    if (pile.current.some((v) => v !== vue && retournable.current(v))) {
      retour();
      return;
    }
    if (secteur?.id === REGIE.id) {
      const connu = lire(CLE_SECTEUR);
      if (connu) {
        try {
          void choisirSecteur(JSON.parse(connu) as Secteur);
          return;
        } catch {
          /* illisible */
        }
      }
    }
    ouvrirArrondissements();
  };
  /** Quitter l'événement pour la ville (bouton « La ville » et retour). */
  const versLaVille = (): void => {
    quitterEvenement();
    const connu = lire(CLE_SECTEUR);
    if (connu) {
      try {
        void choisirSecteur(JSON.parse(connu) as Secteur);
        return;
      } catch {
        /* illisible */
      }
    }
    setVue("proposition");
    ouvrirArrondissements();
  };

  const communs = {
    skin,
    secteur: secteur?.name ?? "Lyon",
    initiale,
    menuOuvert: menu,
    nPoints,
    onSecteur: () => ouvrirArrondissements(),
    onMoi: () => setMenu((m) => !m),
    onPoints: voirPoints,
    onRetour: retourAccueil,
    dessus,
  };

  /*
   * Dans une tribu, la liste des tribus (du quartier, ou les tables de
   * l'événement) s'ouvre DANS l'écran de la tribu : première étape de sa
   * frise. Plus d'écran « autres tribus » à part, dont les étapes « chat » et
   * « rendez-vous » n'étaient que des images qu'on ne pouvait pas toucher.
   * Tout ce qui menait à la liste y mène : retour, « Autres quartiers »,
   * l'écran d'un événement.
   */
  const evtDeLaTribu = enTribu && dansUnEvenement() ? (evenements.find((x) => x.id === evenementCourant()?.id) ?? null) : null;
  const choixOuvert =
    enTribu && !depart && (vue === "tribus" || vue === "autres" || vue === "proposition" || vue === "evenement");
  const fermerChoix = (): void => {
    if (pile.current[pile.current.length - 1] === "tribu") retour();
    else remplacer("tribu");
  };
  const ouvrirChoix = (): void => {
    // Une table d'événement dont on n'a pas encore les tables : on les charge.
    if (dansUnEvenement()) {
      if (!evtDeLaTribu) void revenirALEvenement();
      else setVue("tribus");
      return;
    }
    // Les tribus du quartier de la tribu, même si l'on a regardé ailleurs
    // entre-temps, relues à l'ouverture (puis toutes les dix secondes).
    const s = secteurDeLaTribu();
    if (s && s.id !== secteur?.id) {
      void choisirSecteur(s, true);
      return;
    }
    if (s) void chargerSessions(s);
    setVue("tribus");
  };
  tripleEnTribu.current = enTribu && !depart ? ouvrirChoix : null;

  /** Renoncer pendant l'entrée dans une tribu : on revient à la liste d'où l'on venait. */
  const annulerEntree = (): void => {
    const evt = dansUnEvenement();
    const s = secteurDeLaTribu();
    socket.current?.send({ type: "leave" });
    oublierTribuEnCours();
    remettreAZero();
    if (evt) void revenirALEvenement();
    else if (s) void choisirSecteur(s, true);
    else accueil();
  };
  // Le retour du téléphone fait la même chose que le bouton de l'écran d'entrée.
  retourSpecial.current = vue === "tribu" && !tribe && socket.current !== null && !fin && !annulee ? annulerEntree : null;
  const choix: ChoixTribu | undefined =
    enTribu && tribe
      ? {
          titre: evtDeLaTribu ? evtDeLaTribu.nom : `Tribus ${duSecteur(secteur?.name ?? "Lyon")}`,
          lignes: evtDeLaTribu
            ? evtDeLaTribu.tables
                .filter((x) => x.sessionId !== sessionJointe?.id)
                .sort((a, b) => a.rdvAt - b.rdvAt || a.nom.localeCompare(b.nom, "fr", { numeric: true }))
                .map((x) => ({
                  id: x.sessionId,
                  titre: `${x.nom}${x.sujet && x.sujet !== x.nom ? ` · ${x.sujet}` : ""}`,
                  sous: `${x.point ? `${x.point}, ` : ""}${heure(x.rdvAt)}`,
                  couleur: x.couleur,
                  compte: x.participants >= x.maximum ? "complet" : `${x.participants}/${x.maximum}`,
                  pleine: x.participants >= x.maximum,
                }))
            : sessions
                .filter((x) => x.id !== sessionJointe?.id)
                .sort((a, b) => a.startsAt - b.startsAt)
                .map((x) => ({
                  id: x.id,
                  titre: x.subject ?? x.name,
                  sous: `${x.lieu ?? x.sectorName} · ${aHeure(x.startsAt, maintenant).replace(/^à /, "")}`,
                  couleur: x.color ?? null,
                  compte: x.participants >= x.maxParticipants ? "complet" : `${x.participants}/${x.maxParticipants}`,
                  pleine: x.participants >= x.maxParticipants,
                })),
          ouverte: choixOuvert,
          onOuvrir: ouvrirChoix,
          onFermer: fermerChoix,
          onChoisir: choisirTribu,
          onCreer: evtDeLaTribu || secteur?.id === REGIE.id ? undefined : () => setVue("creer"),
          table: dansUnEvenement(),
        }
      : undefined;

  if (vue === "chargement") {
    return (
      <Ecran skin={skin} label="Chargement">
        <div className="hoa-fil">
          <div className="hoa-frappe" aria-label="Hoa arrive">
            <i />
            <i />
            <i />
          </div>
        </div>
      </Ecran>
    );
  }

  if (vue === "intro") {
    return (
      <Intro
        skin={skin}
        pseudo={pseudo}
        onFermer={
          introRejouee
            ? () => {
                setIntroRejouee(false);
                retour();
              }
            : undefined
        }
        onFin={(p, choix) => {
          changerPseudo(p);
          if (introRejouee) {
            // Revue : le nom est gardé ; dans une tribu, on y revient sans toucher au lieu.
            setIntroRejouee(false);
            if (enTribu) {
              retour();
              return;
            }
          }
          finIntro(() => {
            if (choix === "localiser") {
              void localiser();
              return;
            }
            setVue("proposition");
            ouvrirArrondissements();
          });
        }}
      />
    );
  }

  if (vue === "evenement" && !choixOuvert && ESPACES_HUMAINS && evenements.length === 0) {
    return <EcranSansEvenement skin={skin} dessus={dessus} />;
  }
  if (vue === "evenement" && !choixOuvert) {
    const e = evenements.find((x) => x.id === evenementId) ?? evenements[0];
    if (e) {
      return (
        <EcranEvenement
          skin={skin}
          evenement={e}
          autres={evenements}
          maTable={enTribu && sessionJointe ? sessionJointe.id : null}
          dessus={dessus}
          onChoisir={choisirTribu}
          onAutre={(id) => montrerEvenements(evenements, id)}
          onVille={versLaVille}
          onRetour={() => {
            // Revenir en arrière, c'est sortir de l'événement : sa voix s'en va avec.
            if (pile.current.some((v) => v !== "evenement" && retournable.current(v))) {
              quitterEvenement();
              retour();
            } else versLaVille();
          }}
        />
      );
    }
  }

  // Une suspension ou un bannissement en cours (H-Suspendu) : à la place
  // des écrans d'accueil, pas pendant une tribu déjà commencée.
  const accueilVue = vue === "proposition" || vue === "autres" || vue === "quitte" || vue === "creer" || vue === "evenement" || vue === "suspendu";
  if (sanction && (sanction.type === "suspendre" || sanction.type === "bannir") && accueilVue && !enTribu) {
    return (
      <Suspendu
        skin={skin}
        sanction={sanction}
        envoi={envoiCompte.en}
        erreur={envoiCompte.erreur}
        onContester={(texte) => {
          const compte = props.account;
          if (!compte) return;
          void agirCompte(async () => {
            await contesterServeur(compte, texte);
            await verifierCompte(compte);
          });
        }}
        onRegles={() => setVue("regles")}
        onAccueil={() => ouvrir("moi")}
      />
    );
  }

  if (vue === "moi") {
    return (
      <Moi
        skin={skin}
        pseudo={pseudo}
        soirees={soireesAffichees.length}
        rencontres={soireesAffichees.filter((x) => x.reunis).length}
        points={nPoints}
        badges={profil ? { obtenus: profil.badges.filter((b) => b.obtenu).length } : null}
        photo={photo}
        avecCompte={Boolean(props.account?.email)}
        envoiPhoto={envoiPerso.en}
        erreurPhoto={envoiPerso.erreur}
        dessus={
          feuilleSoirees ? (
            <FeuilleSoirees
              soirees={soireesAffichees}
              onFermer={() => setFeuilleSoirees(false)}
              onSoiree={(x) => {
                setRecap(x);
                setVue("recap");
              }}
            />
          ) : undefined
        }
        onParametres={() => setVue("parametres")}
        onSoirees={() => setFeuilleSoirees(true)}
        onPersonnaliser={() => {
          setEnvoiPerso({ en: false, erreur: null });
          setVue("personnaliser");
        }}
        onBoutique={() => {
          setEnvoiBoutique({ en: false, erreur: null });
          setVue("boutique");
        }}
        onBadges={() => setVue("badges")}
        onPseudo={changerPseudo}
        onPhoto={(p) => {
          const compte = props.account?.email ? props.account : null;
          setEnvoiPerso({ en: true, erreur: null });
          void (async () => {
            if (compte) await photoDuCompte(compte, p);
            poserPhoto(p);
          })().then(
            () => setEnvoiPerso({ en: false, erreur: null }),
            (e: Error) => setEnvoiPerso({ en: false, erreur: e.message }),
          );
        }}
        onCreerCompte={() => versCompte("compte")}
        onConnexion={() => versCompte("connexion")}
        onRetour={retour}
      />
    );
  }

  if (vue === "personnaliser") {
    const compte = props.account?.email ? props.account : null;
    const agirPerso = (f: () => Promise<void>): void => {
      setEnvoiPerso({ en: true, erreur: null });
      void f().then(
        () => setEnvoiPerso({ en: false, erreur: null }),
        (e: Error) => setEnvoiPerso({ en: false, erreur: e.message }),
      );
    };
    return (
      <Personnaliser
        skin={skin}
        profil={profil}
        envoi={envoiPerso.en}
        erreur={envoiPerso.erreur}
        onSkin={changerSkin}
        onPorter={(a) => {
          if (!compte) return;
          agirPerso(async () => poserProfil(await boutiqueServeur(compte, "porter", a.id), sanctionCourante()));
        }}
        onBoutique={() => {
          setEnvoiBoutique({ en: false, erreur: null });
          setVue("boutique");
        }}
        onCompte={() => versCompte("compte")}
        onRetour={retour}
      />
    );
  }

  if (vue === "compte") {
    return (
      <CreerCompte
        skin={skin}
        envoi={envoiCompte}
        onCreer={(e) =>
          void agirCompte(async () => {
            const r = await creerCompte(props.account, { ...e, pseudo });
            props.onAccount(r.compte);
            setCleMontree({ cle: r.cleSecours, nouvelle: false });
            remplacer("cle");
          })
        }
        onConnexion={() => versCompte("connexion")}
        onRetour={retour}
      />
    );
  }

  if (vue === "connexion") {
    return (
      <Connexion
        skin={skin}
        envoi={envoiCompte}
        onConnecter={(e) =>
          void agirCompte(async () => {
            const r = await connecterCompte(props.account, { ...e, pseudoLocal: pseudo });
            retrouve(r.compte, r.pseudo);
            retour();
          })
        }
        onOubli={() => versCompte("secours")}
        onCreer={() => versCompte("compte")}
        onRetour={retour}
      />
    );
  }

  if (vue === "secours") {
    return (
      <Secours
        skin={skin}
        envoi={envoiCompte}
        onRemplacer={(e) =>
          void agirCompte(async () => {
            const r = await secoursCompte(props.account, { ...e, pseudoLocal: pseudo });
            retrouve(r.compte, r.pseudo);
            setCleMontree({ cle: r.cleSecours, nouvelle: true });
            remplacer("cle");
          })
        }
        onRetour={retour}
      />
    );
  }

  if (vue === "cle" && cleMontree) {
    return (
      <CleSecours
        skin={skin}
        cle={cleMontree.cle}
        nouvelle={cleMontree.nouvelle}
        onSuite={() => {
          setCleMontree(null);
          depiler();
        }}
      />
    );
  }

  if (vue === "parametres") {
    const compte = props.account?.email ? props.account : null;
    return (
      <Parametres
        skin={skin}
        email={compte?.email ?? null}
        onCreerCompte={() => versCompte("compte")}
        onConnexion={() => versCompte("connexion")}
        onDeconnexion={() => {
          if (!compte) return;
          void deconnecterCompte(compte).then(props.onAccount, () => undefined);
        }}
        onSupprimer={() => {
          setEnvoiCompte({ en: false, erreur: null });
          setSuppression(true);
        }}
        onEffacerAppareil={() => setEffacement(true)}
        dessus={
          effacement ? (
            <FeuilleEffacerAppareil
              avecCompte={Boolean(compte?.email)}
              enTribu={enTribu}
              onFermer={() => setEffacement(false)}
              onEffacer={() => {
                // Quitter la tribu d'abord : la place est rendue tout de suite.
                try {
                  socket.current?.send({ type: "leave" });
                } catch {
                  /* hors ligne */
                }
                socket.current?.close();
                void effacerAppareil().then(() => location.replace("/"));
              }}
            />
          ) : suppression && compte ? (
            <FeuilleSupprimer
              envoi={envoiCompte}
              onFermer={() => setSuppression(false)}
              onSupprimer={(mdp) =>
                void agirCompte(async () => {
                  props.onAccount(await supprimerCompte(compte, mdp));
                  setSuppression(false);
                })
              }
            />
          ) : (feuilleSignal ?? undefined)
        }
        onRegles={() => setVue("regles")}
        onIntro={() => {
          setIntroRejouee(true);
          setVue("intro");
        }}
        onRetour={retour}
        onExporter={() => {
          if (!compte) return;
          void exporterCompte(compte).then(
            (d) => {
              const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "hoa-mes-donnees.json";
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.setTimeout(() => URL.revokeObjectURL(url), 5000);
            },
            () => setMessage("Le serveur ne répond pas. Réessaie dans un instant."),
          );
        }}
        onSignaler={() => ouvrirSignalSalle(null)}
      />
    );
  }

  if (vue === "regles") {
    return <ReglesHoa skin={skin} onRetour={retour} />;
  }

  if (vue === "recap" && recap) {
    return (
      <Recap
        skin={skin}
        soiree={recap}
        dessus={feuilleSignal ?? undefined}
        onRetour={retour}
        onRelire={() => {
          setRelue(recap);
          setVue("relire");
        }}
        onSignaler={() => ouvrirSignalSalle(recap)}
      />
    );
  }

  if (vue === "relire" && relue) {
    return <RelireSoiree skin={skin} titre={relue.titre} lignes={relue.chat ?? []} moi={relue.moi ?? null} onRetour={retour} />;
  }

  if (vue === "badges") {
    return <Badges skin={skin} badges={profil?.badges ?? null} onRetour={retour} onCompte={() => versCompte("compte")} />;
  }

  if (vue === "boutique") {
    const compte = props.account?.email ? props.account : null;
    return (
      <Boutique
        skin={skin}
        profil={profil}
        envoi={envoiBoutique.en}
        erreur={envoiBoutique.erreur}
        onRetour={retour}
        onCompte={() => versCompte("compte")}
        onPrendre={(a) => {
          if (!compte) return;
          const action = profil?.possessions.includes(a.id) ? "porter" : "prendre";
          setEnvoiBoutique({ en: true, erreur: null });
          void boutiqueServeur(compte, action, a.id).then(
            (p) => {
              poserProfil(p, sanctionCourante());
              setEnvoiBoutique({ en: false, erreur: null });
            },
            (e: Error) => setEnvoiBoutique({ en: false, erreur: e.message }),
          );
        }}
      />
    );
  }

  if (vue === "prevenu" && planning?.prochaine) {
    return (
      <Prevenu
        skin={skin}
        session={planning.prochaine}
        maintenant={maintenant}
        barre={<BarreSoiree secteur={secteur?.name ?? "Lyon"} initiale={initiale} nPoints={nPoints} menuOuvert={menu} onSecteur={() => ouvrirArrondissements()} onMoi={() => setMenu((m) => !m)} onPoints={voirPoints} onRetour={retour} />}
        dessus={dessus}
        onRetour={retour}
      />
    );
  }

  if (vue === "creer" && !props.account?.email) {
    return <CompteRequis skin={skin} onCreerCompte={() => versCompte("compte")} onRetour={retour} />;
  }

  if (vue === "creer" && secteur) {
    return (
      <Creer
        skin={skin}
        secteurId={secteur.id}
        maintenant={maintenant}
        planning={planning}
        envoi={envoiCreation}
        erreur={erreurCreation}
        onRetour={retour}
        onCreer={(c) => void creer(c)}
      />
    );
  }

  if (vue === "tribu" || choixOuvert) {
    const titre = tribe?.title ?? sessionJointe?.subject ?? sessionJointe?.name ?? "Ta tribu";
    if (annulee) {
      const s = secteurDeLaTribu();
      return (
        <Annulee
          skin={skin}
          titre={titre}
          presents={annulee.presents}
          minimum={annulee.minimum}
          restantes={sessions.filter((x) => x.id !== sessionJointe?.id).length}
          duSecteur={duSecteur(s?.name ?? "Lyon")}
          initiale={initiale}
          nPoints={nPoints}
          onPoints={voirPoints}
          menuOuvert={menu}
          dessus={dessus}
          onMoi={() => setMenu((m) => !m)}
          onTribus={() => {
            const evt = dansUnEvenement();
            remettreAZero();
            if (evt) void revenirALEvenement();
            else if (s) void choisirSecteur(s, true);
            else accueil();
          }}
        />
      );
    }
    if (fin) {
      return (
        <Garder
          skin={skin}
          pseudo={pseudo}
          reunis={fin.reunis}
          avecCompte={Boolean(props.account?.email)}
          table={dansUnEvenement()}
          gain={props.account?.email ? (garde?.gain ?? null) : (soirees.find((x) => x.id === sessionJointe?.id)?.gain ?? 0)}
          nouveaux={garde?.nouveaux ?? []}
          total={nPoints}
          onTerminer={() => {
            setGarde(null);
            remettreAZero();
            accueil();
          }}
          onCompte={() => {
            setGarde(null);
            remettreAZero();
            // Une fois le compte créé, « retour » mène à Mon compte.
            remplacer("moi");
            versCompte("compte");
          }}
        />
      );
    }
    if (besoinPseudo || !tribe || !experience) {
      return (
        <EntreeTribu
          skin={skin}
          titre={titre}
          session={sessionJointe}
          besoinPseudo={besoinPseudo}
          pseudo={pseudo}
          erreur={erreurPseudo}
          onPseudo={(p) => {
            changerPseudo(p);
            setErreurPseudo(null);
            socket.current?.send({ type: "set_pseudo", pseudo: p });
          }}
          onAnnuler={annulerEntree}
        />
      );
    }
    const tribuEcran = (
      <Tribu
        skin={skin}
        tribe={tribe}
        stage={stage}
        pid={pid}
        rejointA={rejointA}
        questions={experience.waitingQuestions}
        repondues={repondues}
        chat={chat}
        points={points}
        distance={distance}
        fix={fix}
        maintenant={maintenant}
        erreur={erreurTribu}
        initiale={initiale}
        nPoints={nPoints}
        gain={gainPour(soirees, tribe.rdvAt, tribe.id)}
        plan={evtDeLaTribu?.plan && sessionJointe ? { plan: evtDeLaTribu.plan, sessionId: sessionJointe.id } : null}
        onPoints={voirPoints}
        menuOuvert={menu}
        signalerOuvert={signaler}
        dessus={dessus}
        onMoi={() => setMenu((m) => !m)}
        onRetour={choixOuvert ? fermerChoix : ouvrirChoix}
        choix={choix}
        onFermerSignaler={() => setSignaler(false)}
        onRepondre={(questionId, choice) => {
          setRepondues((r) => (r.includes(questionId) ? r : [...r, questionId]));
          socket.current?.send({ type: "answer_waiting", questionId, choice });
        }}
        onEnvoyer={(text) => socket.current?.send({ type: "chat", text })}
        onJySuis={() => socket.current?.send({ type: "i_am_here" })}
        onTerminer={() => socket.current?.send({ type: "done" })}
        onSignaler={(s) => socket.current?.send({ type: "report", ...s })}
        onQuitter={quitter}
      />
    );
    if (!modeDemo || !sessionJointe) return tribuEcran;
    return (
      <>
        {tribuEcran}
        <BoutonDemo
          sessionId={sessionJointe.id}
          pid={pid}
          fini={stage === "gathered" && tribe.members.every((m) => m.arrive)}
          etapeLocale={() => {
            // Une question pas encore répondue : on y répond (premier choix) à sa place.
            const q = experience.waitingQuestions.find((x) => !repondues.includes(x.id));
            if (!q) return false;
            setRepondues((r) => (r.includes(q.id) ? r : [...r, q.id]));
            socket.current?.send({ type: "answer_waiting", questionId: q.id, choice: "a" });
            return true;
          }}
          apres={() => {
            // Au rendez-vous : les visiteurs simulés arrivent, puis soi-même.
            if (stage === "rendezvous") window.setTimeout(() => socket.current?.send({ type: "i_am_here" }), 300);
          }}
          onRejoue={() => {
            oublierTribuEnCours();
            remettreAZero();
            void revenirALEvenement();
          }}
        />
      </>
    );
  }

  /* Sans quartier : l'écran grisé, la feuille des arrondissements par-dessus. */
  if (!secteur) {
    return <FondGrise skin={skin} lignes={["Ta soirée", "Choisis ta tribu"]} dessus={dessus} />;
  }

  const autresQueLaMienne = sessions.filter((s) => !(enTribu && s.id === sessionJointe?.id));
  // Pendant les cinq secondes d'un départ, la tribu n'est plus « la sienne » dans la liste.
  const maTribu = enTribu && tribe && !depart ? { titre: tribe.title, couleur: tribe.color, onRevenir: () => setVue("tribu") } : null;

  if (vue === "quitte") {
    return (
      <Quitte
        {...communs}
        restantes={autresQueLaMienne.filter((s) => s.id !== sessionJointe?.id).length}
        annulerDans={depart ? Math.max(1, Math.ceil((depart.jusqua - Date.now()) / 1000)) : null}
        onVoir={() => setVue("autres")}
        onAnnuler={annulerDepart}
      />
    );
  }

  const proposee = meilleureTribu(autresQueLaMienne);
  const enRegie = secteur.id === REGIE.id;

  // Dans une tribu, pas de proposition : la liste, avec la sienne en tête.
  // L'espace régie n'a pas d'écran « Hoa dort » : la liste, vide ou non.
  if (vue === "autres" || (maTribu && vue === "proposition") || (enRegie && !proposee)) {
    return (
      <Autres
        {...communs}
        sessions={maTribu ? autresQueLaMienne : autresQueLaMienne.filter((s) => s.id !== proposee?.id)}
        maintenant={maintenant}
        maTribu={maTribu}
        onChoisir={choisirTribu}
        onCreer={enRegie ? undefined : () => setVue("creer")}
      />
    );
  }

  // Hors des sessions de la ville, Hoa dort (H-HorsSession) : on se fait
  // prévenir, ou l'on crée une tribu pour la prochaine session.
  if (!proposee || (planning && !planning.ouvert && !maTribu && !enRegie)) {
    return (
      <HorsSession
        {...communs}
        message={message}
        planning={planning}
        maintenant={maintenant}
        pretes={autresQueLaMienne.length}
        onCreer={() => setVue("creer")}
        onVoir={() => setVue("autres")}
        onPrevenir={() => {
          if (planning?.prochaine) {
            ecrireSessionPrevenue(planning.prochaine);
            setVue("prevenu");
          }
        }}
      />
    );
  }

  return (
    <Proposition
      {...communs}
      proposee={proposee}
      autres={autresQueLaMienne.length - 1}
      maintenant={maintenant}
      message={message}
      regie={enRegie}
      onJyVais={() => choisirTribu(proposee.id)}
      onUneAutre={() => setVue(autresQueLaMienne.length > 1 ? "autres" : "creer")}
    />
  );
}

/**
 * L'entrée dans une tribu : le temps de la connexion, et le pseudo quand on
 * ne le connaît pas encore.
 */
function EntreeTribu(props: {
  skin: SkinId;
  titre: string;
  session: SessionSummary | null;
  besoinPseudo: boolean;
  pseudo: string;
  erreur: string | null;
  onPseudo: (p: string) => void;
  onAnnuler: () => void;
}): JSX.Element {
  const [valeur, setValeur] = useState(props.pseudo);
  const table = props.session?.id.startsWith("evt-") ?? false;
  return (
    <Ecran skin={props.skin} label="Entrée dans la tribu">
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onAnnuler}>
          <Retour />
        </button>
        <span className="titre">{props.titre}</span>
      </Barre>
      <div className="hoa-frise">
        <Etape etat="courante" expression="sourire">
          {props.besoinPseudo ? (
            <>
              <Bulle>{table ? "Comment les autres t'appellent ?" : "Comment les autres t'appellent ce soir ?"}</Bulle>
              {/* Pas de <form> ni d'<input> : sur iPhone, un champ dans un formulaire
                  fait apparaître la barre ‹ › ✓ au-dessus du clavier, et Chrome sur
                  Android pose sa barre clé / carte / adresse sur les <input>. */}
              <div className="hoa-saisie">
                <textarea
                  className="hoa-champ hoa-champ-chat"
                  aria-label="Pseudo"
                  placeholder="Un prénom, un surnom"
                  maxLength={24}
                  rows={1}
                  name="hoa-pseudo"
                  autoComplete="off"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="done"
                  autoFocus
                  value={valeur}
                  onChange={(e) => setValeur(e.target.value.replace(/\n/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      const p = valeur.trim();
                      if (p.length >= 2) props.onPseudo(p);
                    }
                  }}
                />
              </div>
              {props.erreur ? (
                <div className="hoa-erreur" role="alert" style={{ marginTop: 10 }}>
                  {props.erreur}
                </div>
              ) : null}
              <p className="hoa-note" style={{ marginTop: 10 }}>
                18 ans ou plus.
              </p>
              <button
                type="button"
                className="hoa-go"
                style={{ marginTop: 12 }}
                disabled={valeur.trim().length < 2}
                onClick={() => props.onPseudo(valeur.trim())}
              >
                C'est moi
              </button>
              <button type="button" className="hoa-lien" onClick={props.onAnnuler}>
                {table ? "Choisir une autre table" : "Choisir une autre tribu"}
              </button>
            </>
          ) : (
            <Bulle>
              J'ouvre la porte de la {table ? "table" : "tribu"}
              {props.session ? ` — rendez-vous à ${heure(props.session.startsAt)}` : ""}…
            </Bulle>
          )}
        </Etape>
        <EtapesAVenir table={table} />
      </div>
    </Ecran>
  );
}
